import { useCallback, useEffect, useState } from 'react';
import { connect } from 'redux-bundler-react';
import StepHeader from '@components/step-header/StepHeader';
import DatePicker from '@components/date-picker/DatePicker';
import TextInput from '@components/text-input/TextInput';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Section from '@components/section/Section';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { Alert, Button } from '@trussworks/react-uswds';
import { filterNullEmptyObjects, doClearIncompletePhoneInfo } from '@src/utils/helpers';
import { useJpa, useRequest, useUserRoleCapabilities } from '@stores';
import { useUserProfile, useResetDirtyFields } from '@hooks';
import { formatJpaRequestAnswerSet } from '@api/services/jpa/helpers';
import { getRequestOwner } from '@api/services/authorized-user/helper';
import { AuthorizedUserPermissions } from '@api/services/authorized-user/types';
import { RequestStatus, RequestType } from '@src/utils/enums';
import useGetAuthorizedUsers from '@api/services/authorized-user/hooks/useGetAuthorizedUsers';
import { useLookupTableFormVersions } from '@api/services/lookup/hooks';
import { useIsAquaticResourceValidationFetching } from '@api/services/aquatic-resources/hooks';
import CertifySignSubmitRoleAlert from '@forms/input-forms/common/CertifySignSubmitRoleAlert';
import Loader from '@components/loader/Loader';

export const PermitsFormCertifySignSubmitMetadata = {
  sectionName: 'Certify, Sign, and Submit',
};

// map jpa questions id errors to question text
const mapErrorsToQuestions = (permitErrors, jpaQuestionText) => {
  if (!jpaQuestionText || !Array.isArray(permitErrors)) return [];

  const errorsWithQuestions = new Map();

  permitErrors.forEach(({ errors, details }) => {
    details?.jpaQuestionIDs?.forEach((questionID) => {
      if (jpaQuestionText[questionID]) {
        if (!errorsWithQuestions.has(questionID)) {
          errorsWithQuestions.set(questionID, {
            questionID,
            questionText: jpaQuestionText[questionID],
            errorMessages: new Set(),
          });
        }
        Object.entries(errors).forEach(([errorMsg, errorCount]) => {
          if (errorCount > 0) errorsWithQuestions.get(questionID).errorMessages.add(errorMsg);
        });
      }
    });
  });

  return Array.from(errorsWithQuestions.values()).map((entry) => ({
    ...entry,
    errorMessages: Array.from(entry.errorMessages),
  }));
};

const updatePermitErrorsWithQuestions = (permitErrors, mappedErrors) => {
  if (!mappedErrors || mappedErrors.length === 0) return permitErrors;

  return permitErrors.map((section) => {
    if (section.details?.jpaQuestionIDs) {
      return {
        sectionTitle: section.sectionTitle,
        errors: mappedErrors
          .filter((error) => section.details.jpaQuestionIDs.includes(error.questionID))
          .reduce((acc, error) => {
            acc[error.questionText] = 'is required';
            return acc;
          }, {}),
      };
    }
    return section;
  });
};

const PermitsFormCertifySignSubmit = connect(
  'doUpdateRequestObject',
  'doUploadAndUpdatePermits',
  'selectRequestAPIData',
  'selectRequestFormData',
  'selectPermitErrors',
  'selectRequestID',
  'selectRequestStatus',
  ({
    doUpdateRequestObject,
    doUploadAndUpdatePermits,
    requestAPIData,
    requestFormData,
    permitErrors,
    stepNo,
    requestID,
    version,
    requestStatus,
  }) => {
    const { data: authorizedUsersData } = useGetAuthorizedUsers({ requestId: requestID, version });
    const isArValidationFetching = useIsAquaticResourceValidationFetching(requestID);
    const { jpaQuestionText } = useJpa();
    const { userProfileData } = useUserProfile();
    const lookupTableFormVersions = useLookupTableFormVersions();
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched, sections } = useRequest();
    const canSign = useUserRoleCapabilities((state) => state.roleCapabilities.signSubmitRequest);
    const authorizedUsers = authorizedUsersData ?? [];
    const requestOwner = getRequestOwner(authorizedUsers);
    const isRequestOwner = requestAPIData?.request?.permission === AuthorizedUserPermissions.Owner;
    const canUserSign = canSign && isRequestOwner;
    const hasSubmitted = requestStatus !== RequestStatus.Draft;
    const formVersion = lookupTableFormVersions.find(
      (item) => item.requestType === RequestType.PermitApplication
    )?.formVersion;

    const defaultValues = {
      signature: requestAPIData?.request?.signature ?? null,
      signatureDate:
        requestAPIData?.request?.signatureDate !== null
          ? requestAPIData?.request?.signatureDate?.split('T')[0]
          : ('' ?? ''),
    };

    const [isSigned, setIsSigned] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const mappedErrors = mapErrorsToQuestions(permitErrors, jpaQuestionText);

    permitErrors = updatePermitErrorsWithQuestions(permitErrors, mappedErrors);

    const getName = useCallback(
      () =>
        userProfileData.middleName
          ? `${userProfileData.firstName} ${userProfileData.middleName} ${userProfileData.lastName}`
          : `${userProfileData?.firstName} ${userProfileData.lastName}`,
      [userProfileData]
    );

    const schema = yup.object().shape({
      signature: yup.string().test('isSigned', `Signature must be ${getName()}`, (val) => val.trim() === getName()),
      signatureDate: yup.string().required('Please select a date'),
    });
    const methods = useForm({ defaultValues: defaultValues, resolver: yupResolver(schema), mode: 'onBlur' });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      handleSubmit,
      trigger,
      reset,
    } = methods;
    const isDirty = Object.keys(dirtyFields).length > 0;
    const errorCount = Object.keys(errors)?.length;
    const isTouched = isSectionTouched(stepNo);
    const isSubmitDisabled = !isSigned || isReadOnly || !canUserSign || isArValidationFetching;

    //Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    const signature = watch('signature');
    const signatureDate = watch('signatureDate');
    const dateNow = new Date().toISOString().slice(0, 10);

    useEffect(() => {
      if (signature?.trim() === getName()) {
        !signatureDate && setValue('signatureDate', new Date().toISOString().slice(0, 10));
        setIsSigned(true);
        trigger();
      } else {
        setIsSigned(false);
      }
    }, [signature, setValue, signatureDate, trigger, getName]);

    const submitForm = () => {
      const { answerSet, removeDeletedTableAnswers } = useJpa.getState();
      const requestFormDataSubmit = {
        ...requestFormData,
        request: { ...requestFormData.request, isSubmit: true, formVersion: formVersion },
      };
      const cleanedPayload = doClearIncompletePhoneInfo(requestFormDataSubmit);
      const payload = filterNullEmptyObjects(cleanedPayload);
      const updatePayload = formatJpaRequestAnswerSet(payload, answerSet);
      doUploadAndUpdatePermits(updatePayload, 'Submit');
      removeDeletedTableAnswers();
    };

    // Consolidated effect: Calculate form validity and update section state
    useEffect(() => {
      const hasInvalidSections = sections.some(
        (section) => !section.hidden && section.valid === false && section.label !== 'Certify, Sign, and Submit'
      );
      const newIsFormValid = !hasInvalidSections;

      // Only update if state actually changed to prevent cascading re-renders
      if (newIsFormValid !== isFormValid) {
        setIsFormValid(newIsFormValid);
      }

      // Update section with all props at once
      updateSection(stepNo, {
        dirty: isDirty,
        valid: newIsFormValid && isSigned,
      });

      // Only update request on successful completion
      if (newIsFormValid && isSigned && signature && signatureDate) {
        doUpdateRequestObject({ signature, signatureDate });
      }
    }, [
      stepNo,
      isDirty,
      isSigned,
      isFormValid,
      signature,
      signatureDate,
      sections,
      doUpdateRequestObject,
      updateSection,
    ]);

    const renderSignatureSection = () => {
      if (!isFormValid) {
        return (
          <Alert className='margin-top-3' slim type='error'>
            In order to submit this form, please revisit the section(s) marked with errors, and correct any missing or
            invalid fields.
          </Alert>
        );
      }

      return (
        <Section label='Signature'>
          <div className='grid-row grid-gap-1'>
            <div className='grid-col-6'>
              <TextInput
                name='signature'
                label='Signature'
                signature={canUserSign && getName()}
                required
                readOnly={isReadOnly || !canUserSign}
              />
            </div>
            <div className='grid-col-6'>
              <DatePicker
                min={null}
                max={dateNow}
                name='signatureDate'
                label='Signature Date'
                readOnly={isReadOnly || !canUserSign}
                required
              />
            </div>
          </div>
          <hr />
          <div className='display-flex flex-align-center flex-justify-start padding-top-1'>
            <Button
              className='submit-request-button'
              title='Submit Request'
              disabled={isSubmitDisabled}
              onClick={handleSubmit(submitForm)}
            >
              Submit Request
            </Button>
            {isArValidationFetching && (
              <div className='margin-left-2'>
                <Loader variant='inline' loaderText='Validating aquatic resources...' />
              </div>
            )}
          </div>
        </Section>
      );
    };
    return (
      <FormProvider {...methods}>
        {(!isFormValid || errorCount > 0) && !isReadOnly && <ErrorSummary errors={errors} type='form' />}
        {permitErrors && Object.keys(permitErrors)?.length > 0 && (
          <ErrorSummary errors={permitErrors} type='api-validation' />
        )}
        <CertifySignSubmitRoleAlert showAlert={!hasSubmitted && !canSign} />
        {!isRequestOwner && !hasSubmitted && (
          <Alert slim className='margin-top-3 margin-bottom-3' type='warning'>
            You are not authorized to Certify, Sign and Submit this request. Only the{' '}
            <span className='text-bold'>
              Request Owner: {requestOwner?.firstName} {requestOwner?.lastName}
            </span>{' '}
            can perform this operation.
          </Alert>
        )}
        <StepHeader text='Certify, Sign, and Submit'>
          <b>
            The Application must be signed by the person who desires to undertake the proposed activity (applicant) or
            it may be signed by a duly authorized agent if a completed agent authorization is provided. A Department of
            the Army Permit (DA) permit does not convey any property rights, either in real estate or material, or any
            exclusive privileges. Furthermore, a DA permit does not authorize any injury to property or invasion of
            rights or any infringement of Federal, state or local laws or regulations. The applicant's signature on an
            application is an affirmation that the applicant possesses or will possess the requisite property interest
            to undertake the activity proposed in the application. The district engineer will not enter into disputes
            but will remind the applicant of the above. The dispute over property ownership will not be a factor in the
            USACE public interest decision (33 FR 320.4(g)(6))
          </b>
        </StepHeader>

        <h6>BY SUBMITTING THIS REQUEST, I AM AGREEING TO ALL OF THE FOLLOWING:</h6>
        <ul className='padding-left-3 text-bold'>
          <li>
            I consent to conduct business electronically and understand this consent can be withdrawn at any time in
            writing to the local USACE Regulatory office.
          </li>
          <li>
            Application is hereby made for permit or permits to authorize the work described in this application and all
            supporting documents submitted with the application.{' '}
          </li>
          <li>I certify that the information in this application is complete and accurate. </li>
          <li>
            I understand that the signature of the applicant or the agent will be an affirmation that the applicant
            possesses or will possess the requisite property interest to undertake the activity proposed in the
            application.
          </li>
          <li>
            I hereby certify that I have included all activities that require a USACE permit that I plan to undertake
            and that are reasonably related to the project proposed in this permit application.
          </li>
          <li>
            I understand that, whoever, in any matter within the jurisdiction of any department of agency of the United
            States, knowingly and willfully (1) falsifies, conceals, or covers up by any trick, scheme, or device a
            material fact; (2) makes any materially false, fictitious, or fraudulent statement or representation; or (3)
            makes or uses any false writing or document knowing the same to contain any materially false, fictitious, or
            fraudulent statement or entry; shall be fined not more than $10,000 or imprisoned not more than 5 years or
            both. (18 USC Section 1001)
          </li>
          <li>
            I understand that by typing my name in the text field below that I am e-signing this application and I
            understand that this e-signature has the same legal status as handwritten signatures.
          </li>
        </ul>

        {renderSignatureSection()}
      </FormProvider>
    );
  }
);

PermitsFormCertifySignSubmit.metadata = PermitsFormCertifySignSubmitMetadata;

export default PermitsFormCertifySignSubmit;
